import { z } from 'zod';
const CreativitySchema = z.enum(['precise', 'neutral', 'creative']);
/**
 * Exported so the composer can stop the user at the same length the schema refuses.
 *
 * The input is validated by a guardrail that trips the whole turn, so a longer objective does not
 * degrade to a truncated one: it fails the request.
 */
export const COPY_GOALS_MAX_LENGTH = 500;
const CopywritingChannelSchema = z.enum([
    'plain',
    'instagram',
    'twitter',
    'tiktok',
    'facebook',
    'email',
]);
export const CopyGenerationInputSchema = z.object({
    prompt: z.string().describe('The text prompt describing the copy to generate'),
    channels: z
        .array(CopywritingChannelSchema)
        .min(1)
        .default(['plain'])
        .describe('One or more channels for which to generate copy'),
    creativity: CreativitySchema.default('neutral').describe('Level of creativity to use in copywriting'),
    goals: z
        .string()
        .min(1)
        .max(COPY_GOALS_MAX_LENGTH)
        .optional()
        .describe('Primary objective or goal of the copywriting task'),
    variations: z
        .number()
        .int()
        .min(1)
        .max(10)
        .default(1)
        .describe('Number of variations to generate'),
    enableQAFeedback: z
        .boolean()
        .default(false)
        .describe('Enable QA')
        .describe('Automatically evaluate and refine copy based on audience resonance (score threshold: 80, max 5 iterations). This will take longer to generate.'),
});
export const QAMetadataSchema = z.object({
    finalScore: z.number().int().min(0).max(100).describe('Final resonance score'),
    iterationCount: z.number().int().min(0).max(5).describe('Number of iterations performed'),
    feedback: z.array(z.string()).describe('Feedback from each iteration'),
    targetAudience: z.string().describe('The target audience for the copy'),
});
const PlainTextSchema = z.object({
    channel: z.literal('plain'),
    text: z.string().describe('Output copy for plain text channel'),
    qaMetadata: QAMetadataSchema.optional(),
});
const TwitterSchema = z.object({
    channel: z.literal('twitter'),
    body: z.string().describe('Tweet body text. X (Twitter) posts must be 280 characters or fewer'),
    qaMetadata: QAMetadataSchema.optional(),
});
const InstagramSchema = z.object({
    channel: z.literal('instagram'),
    body: z
        .string()
        .describe('Instagram caption text. Instagram body must be 250 characters or fewer'),
    hashtags: z
        .array(z.string())
        .min(2)
        .max(5)
        .describe('2-5 hashtags, each ≤ 24 characters. Each hashtag must be 24 characters or fewer'),
    qaMetadata: QAMetadataSchema.optional(),
});
const TikTokSchema = z.object({
    channel: z.literal('tiktok'),
    body: z
        .string()
        .describe('TikTok video caption. TikTok captions must be 300 characters or fewer'),
    qaMetadata: QAMetadataSchema.optional(),
});
const FacebookSchema = z.object({
    channel: z.literal('facebook'),
    body: z.string().describe('The facebook post body, maximum of 20 words'),
    headline: z.string().describe('The facebook post headline, maximum of 5 words'),
    linkDescription: z
        .string()
        .describe('The facebook post link description, maximum of 30 characters'),
    qaMetadata: QAMetadataSchema.optional(),
});
const EmailSchema = z.object({
    channel: z.literal('email'),
    subject: z.string().describe('Email subject line: 6-10 words, <=60 chars'),
    preheader: z.string().describe('Email preheader: 40-100 chars, completes the subject thought'),
    heroImageCopy: z.string().describe('Hero image overlay text: 3-6 words, <=10 words'),
    bodyTitle: z.string().describe('Body H1/H2 headline: 5-8 words, <=15 words'),
    bodyCopy: z.string().describe('Body narrative: 50-125 words, <=900 chars, short paragraphs'),
    bodyImageCopy: z.string().describe('Body image alt text: 3-6 words, <=12 words'),
    primaryCta: z.string().describe('Primary CTA button: 2-3 words, <=5 words, action verb'),
    secondaryCta: z.string().describe('Secondary CTA text link: 3-5 words'),
    footer: z.string().describe('Footer copy: 1-2 sentences, brand contact + legal'),
    qaMetadata: QAMetadataSchema.optional(),
});
export const CopyGenerationOutputItemSchema = z.discriminatedUnion('channel', [
    PlainTextSchema,
    TwitterSchema,
    InstagramSchema,
    TikTokSchema,
    FacebookSchema,
    EmailSchema,
]);
export const CopyGenerationWidgetOutputSchema = z.object({
    text: z.string().describe('Summary of the generated copy'),
    title: z
        .string()
        .describe('Short summary for the generated copy to use as the title of the widget'),
    items: z
        .array(CopyGenerationOutputItemSchema)
        .optional()
        .describe('The generated copy items for the requested social channels'),
});
